import { observable, action, computed, toJS, autorun } from "mobx";
import { sceneImages } from "../assets/data/imagesMap";
import { orderByLevelId, getLevelsSetting } from "../helpers/arrayHelpers";
import { fetchData } from "../helpers/apiHelpers.js";
import { stepperAction, goToStep } from "../helpers/stepperHelpers";
import { camelToName } from "../helpers/stringHelpers";
import iconsMap from "../assets/data/iconsMaps";
import sceneImagesMap from "../assets/data/sceneImagesMap"
import setup from '../setup/setup'
import itemsObj from '../assets/data/items'
import atomsObj from '../assets/data/atoms'
import sceneConfigObj from '../assets/data/sceneConfig'

class SceneStore {

  @observable
  sceneConfig = {};

  @observable
  atoms = {};

  @observable
  sceneSetting = {
    stepTotal: 0,
    currentStep: 0,
    currentSubStep: 0,
    completedStep: 0,
    editedStep: [],
    completedStepList: [],
    levelsSetting: []
  };

  @observable
  items = {};

  @action
  setScene = (reset) => {
    const dynamicLevels = this.getDynamicLevels;
    this.sceneSetting.stepTotal = dynamicLevels.length;
    this.sceneSetting.levelsSetting = getLevelsSetting(dynamicLevels);
    if (reset) {
      this.sceneSetting.currentStep = 0;
      this.sceneSetting.currentSubStep = 0;
      this.sceneSetting.currentSubStep = 0;
      this.sceneSetting.completedStep = 0;
      this.sceneSetting.editedStep = [];
      this.sceneSetting.completedStepList= [];
    }
  };

  fetchConfig = autorun(cb => {
    if (!setup.offline){
      fetchData("sceneConfig.json", res => {
        if (res.success) {
          this.sceneConfig = res.data;
          this.setScene();
        } else {
          console.error(res.err);
        }
      });
      fetchData("items.json", res => {
        if (res.success) {
          this.items = res.data;
        } else {
          console.error(res.err);
        }
      });
      fetchData("atoms.json", res => {
        if (res.success) {
          this.atoms = res.data;
        } else {
          console.error(res.err);
        }
      });
    } else {
      this.atoms = { ...atomsObj };
      this.items = { ...itemsObj };
      this.sceneConfig = { ...sceneConfigObj };
      this.setScene();
    }
  });

  @action
  resetScene = () => {
    this.setScene(true)
  }

  @action
  getSceneImages = alias => {
    return sceneImagesMap[alias];
  };

  @action
  getIconImage = alias => {
    return iconsMap[alias] ? iconsMap[alias] : 'test'
  };

  @action
  getIconImageName = alias => {
    return iconsMap[alias] ? iconsMap[alias] : 'test'
  };

  @action
  getItemByAlias = alias => {
    return toJS(this.items[alias])
  }

  @action
  getStepConfiguration = (alias) => {
    const sceneSetting = this.sceneSetting;
    const levelsSetting = sceneSetting.levelsSetting;
    const mySetting = levelsSetting.find(level => ( level.alias === alias ))
    return toJS(mySetting);
  }

  @action
  changeStep = (type, stepId) => {
    const sceneSetting = this.sceneSetting;
    const currentStepLength = this.getCurrentStepLength;
    const getPreviewStepLength = this.getPreviewStepLength;
    if (type !== "direct") {
      this.sceneSetting = stepperAction(sceneSetting, currentStepLength, type, getPreviewStepLength);
    } else {
      this.sceneSetting = goToStep(sceneSetting, stepId);
    }
    document.getElementsByClassName('sidebar-content-in')[0].scrollTo(0,0)
  };

  @action
  onAtomSelect = (atom, type, link) => {
    const currentStep = this.getCurrentStep;
    const currentSubStep = this.getCurrentSubStep
    const currentLevelSetting = this.sceneSetting.levelsSetting[currentStep]
    const currentStepSetting = currentLevelSetting.configuration[currentSubStep]
    currentStepSetting.configuration = { ...atom };
    currentStepSetting.configuration.type = type;
    currentStepSetting.configuration.imgAlias = currentStepSetting.name + type + atom.alias;
    if (!this.sceneSetting.editedStep.includes(currentStepSetting.name)){
      this.sceneSetting.editedStep.push(currentStepSetting.name.toLowerCase())
    }
    
    if (link) {
      const linkedSetting = this.sceneSetting.levelsSetting.find(level => (level.alias === link.toStep));
      const linkedSettingIndex = this.sceneSetting.levelsSetting.findIndex(level => (level.alias === link.toStep));
      const linkedSubStep = this.sceneSetting.levelsSetting[linkedSettingIndex].configuration[0];
      
      if (linkedSubStep.configuration && linkedSubStep.configuration.name){
        linkedSubStep.configuration[link.prop] = currentStepSetting.configuration[link.prop];
        linkedSubStep.configuration.imgAlias = 
          linkedSetting.alias + 
          linkedSubStep.configuration[link.prop] + 
          linkedSubStep.configuration.alias;
      }
    }
  };

  @action
  checkEnabledLevel = (atom, type) => {
    const currentStep = this.getCurrentStep;
    const currentSubStep = this.getCurrentSubStep
    const currentLevelSetting = this.sceneSetting.levelsSetting[currentStep]
    const currentStepSetting = currentLevelSetting.configuration[currentSubStep]
    if (sceneImagesMap[currentStepSetting.name + type + atom.alias]){
      return true
    } else {
      return false
    }
  }

  @computed
  get currentSubStepSelectedAtom(){
    const currentStep = this.getCurrentStep;
    const currentSubStep = this.getCurrentSubStep;
    if (this.sceneSetting.levelsSetting.length){
      return this.sceneSetting.levelsSetting[currentStep].configuration[currentSubStep].configuration
    } else {
      return {}
    }
  }

  @computed
  get currentSubStepName(){
    const currentStep = this.getCurrentStep;
    const currentSubStep = this.getCurrentSubStep;
    if (this.sceneSetting.levelsSetting.length){
      return camelToName(this.sceneSetting.levelsSetting[currentStep].configuration[currentSubStep].name)
    }
  }

  @computed
  get currentSubStepAlias() {
    const sceneSetting = this.sceneSetting;
    const dynamicLevels = this.getDynamicLevels;
    const { currentStep, currentSubStep } = sceneSetting;
    if (this.getDynamicLevels.length) {
      return dynamicLevels[currentStep].steps[currentSubStep];
    } else {
      return "";
    }
  }

  @computed
  get currentSubStepInfo() {
    const itemsArr = Object.values(this.items);
    if (itemsArr.length) {
      return toJS(this.getItems[this.currentSubStepAlias]);
    } else {
      return {};
    }
  }

  @computed
  get currentSubStepConfiguration() {
    const currentSubStepInfo = this.currentSubStepInfo ? this.currentSubStepInfo : {};
    const types = currentSubStepInfo.types;
    if (types) {
      const typeArr = Object.keys(types)
      return typeArr.map(type => ({
        name: types[type].name,
        alias: type,
        atoms: types[type].subTypes.map(subType => {
          return ({
            name: subType,
            list: this.atoms[subType] ? toJS(Object.values(this.atoms[subType].list)) : null,
            visibleName: this.atoms[subType] ? this.atoms[subType].name : null
          })
        })
      }))
    } else {
      return []
    }
  }

  @computed
  get getItems() {
    const itemsArr = Object.values(this.items);
    if (itemsArr.length) {
      return toJS(this.items);
    } else {
      return {};
    }
  }

  @computed
  get getConfig() {
    return toJS(this.sceneConfig);
  }

  @computed
  get getCurrentStep() {
    return this.sceneSetting.currentStep
  }

  @computed
  get getCurrentSubStep() {
    return this.sceneSetting.currentSubStep
  }

  @computed
  get getCurrentStepLength() {
    const dynamicLevels = this.getDynamicLevels;
    const currentStep = this.sceneSetting.currentStep;
    if (dynamicLevels.length) {
      return dynamicLevels[currentStep].steps.length;
    } else {
      return 0;
    }
  }

  @computed
  get getPreviewStepLength() {
    const dynamicLevels = this.getDynamicLevels;
    const currentStep = this.sceneSetting.currentStep;
    if (dynamicLevels.length) {
      return dynamicLevels[currentStep - 1] ? dynamicLevels[currentStep - 1].steps.length : 0;
    } else {
      return 0;
    }
  }

  @computed
  get getSceneSetting() {
    const sceneSetting = this.sceneSetting;
    if (sceneSetting) {
      return toJS(sceneSetting);
    } else {
      return {};
    }
  }

  @computed
  get getSortedDefaultLevels() {
    const { defaultLevels } = this.sceneConfig;
    if (defaultLevels) {
      return orderByLevelId(Object.values(toJS(defaultLevels)));
    } else {
      return [];
    }
  }

  @computed
  get getSortedDynamicLevels() {
    const { dynamicLevels } = this.sceneConfig;
    if (dynamicLevels) {
      return orderByLevelId(Object.values(toJS(dynamicLevels)));
    } else {
      return [];
    }
  }

  @computed
  get getDefaultLevels() {
    const { defaultLevels } = this.sceneConfig;
    if (defaultLevels) {
      return Object.values(toJS(defaultLevels));
    } else {
      return [];
    }
  }

  @computed
  get getDynamicLevels() {
    const { dynamicLevels } = this.sceneConfig;
    if (dynamicLevels) {
      return Object.values(toJS(dynamicLevels));
    } else {
      return [];
    }
  }
}

const store = new SceneStore();

export default store;
